import {
  Calendar,
  Clock,
  Package,
  CheckCircle,
  AlertCircle,
  Truck,
  Building,
  Home,
  Briefcase,
  MapPin,
} from "lucide-react";

export const getStatusConfig = (t) => ({
  SCHEDULED: {
    color: "bg-blue-100 text-blue-800 border-blue-200",
    icon: Calendar,
    label: t("status.scheduled", "Scheduled"),
    canMarkReady: true, // SCHEDULED means user has entered all meals, restaurant can mark as ready
  },
  PENDING: {
    color: "bg-yellow-100 text-yellow-800 border-yellow-200",
    icon: Clock,
    label: t("status.pending", "Pending"),
    canMarkReady: false, // PENDING means user hasn't completed meal selection
  },
  READY_TO_DELIVER: {
    color: "bg-green-100 text-green-800 border-green-200",
    icon: Package,
    label: t("status.readyToDeliver", "Ready to Deliver"),
    canMarkReady: false,
  },
  IN_DELIVERY: {
    color: "bg-purple-100 text-purple-800 border-purple-200",
    icon: Truck,
    label: t("status.inDelivery", "In Delivery"),
    canMarkReady: false,
  },
  DELIVERED: {
    color: "bg-green-100 text-green-800 border-green-200",
    icon: CheckCircle,
    label: t("status.delivered", "Delivered"),
    canMarkReady: false,
  },
  MISSED: {
    color: "bg-red-100 text-red-800 border-red-200",
    icon: AlertCircle,
    label: t("status.missed", "Missed"),
    canMarkReady: false,
  },
});

export const getLocationTypeIcon = (type) => {
  switch (type) {
    case "HOME":
      return Home;
    case "OFFICE":
      return Briefcase;
    case "OTHER":
      return Building;
    default:
      return MapPin;
  }
};

export const formatTimeRange = (timeFrom, timeTo, language = "en") => {
  const formatTime = (time) => {
    if (time === null || time === undefined) return "";

    let hours,
      minutes = 0;

    // Handle different time formats
    if (typeof time === "number") {
      if (time <= 23) {
        // Simple hour format: 12, 15, etc.
        hours = time;
        minutes = 0;
      } else {
        // HHMM format: 1230, 1500, etc.
        hours = Math.floor(time / 100);
        minutes = time % 100;
      }
    } else if (typeof time === "string") {
      if (time.includes(":")) {
        // Format: "14:30" or "14:30:00"
        const timeParts = time.split(":");
        hours = parseInt(timeParts[0], 10);
        minutes = parseInt(timeParts[1], 10);
      } else if (time.length >= 3) {
        // Format: "1430" (string)
        const timeNum = parseInt(time, 10);
        hours = Math.floor(timeNum / 100);
        minutes = timeNum % 100;
      } else {
        // Single or double digit hour as string
        hours = parseInt(time, 10);
        minutes = 0;
      }
    } else {
      return String(time);
    }

    // Validate hours and minutes
    if (
      isNaN(hours) ||
      isNaN(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return String(time);
    }

    // Convert to 12-hour format
    const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const ampm = hours >= 12 ? "PM" : "AM";
    const timeString = `${hour12}:${minutes
      .toString()
      .padStart(2, "0")} ${ampm}`;

    // Convert to Arabic numerals if language is Arabic
    if (language === "ar") {
      return timeString.replace(/\d/g, (digit) => {
        const arabicNumerals = [
          "٠",
          "١",
          "٢",
          "٣",
          "٤",
          "٥",
          "٦",
          "٧",
          "٨",
          "٩",
        ];
        return arabicNumerals[parseInt(digit)];
      });
    }

    return timeString;
  };

  const separator = language === "ar" ? " - " : " - ";
  return `${formatTime(timeFrom)}${separator}${formatTime(timeTo)}`;
};

export const getScheduleStats = (schedules) => {
  return {
    total: schedules.length,
    pending: schedules.filter((s) => s.status === "PENDING").length,
    ready: schedules.filter((s) => s.status === "READY_TO_DELIVER").length,
    inDelivery: schedules.filter((s) => s.status === "IN_DELIVERY").length,
    delivered: schedules.filter((s) => s.status === "DELIVERED").length,
  };
};

export const filterSchedules = (
  schedules,
  statusFilter,
  searchTerm,
  timeSlotFilter = "ALL",
  areaFilter = "ALL"
) => {
  return schedules.filter((schedule) => {
    const matchesStatus =
      statusFilter === "ALL" || schedule.status === statusFilter;

    const matchesTimeSlot =
      timeSlotFilter === "ALL" ||
      `${schedule.timeToServeFrom}-${schedule.timeToServeTo}` ===
        timeSlotFilter;

    const matchesArea =
      areaFilter === "ALL" || schedule.location?.area === areaFilter;

    const matchesSearch =
      !searchTerm ||
      schedule.subscriptionName
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      schedule.meals?.some(
        (meal) =>
          meal.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          meal.nameAr?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          meal.nameEn?.toLowerCase().includes(searchTerm.toLowerCase())
      );

    return matchesStatus && matchesTimeSlot && matchesArea && matchesSearch;
  });
};
